/**
 * PKCE, in about forty lines of WebCrypto.
 *
 * No auth library. The alternatives -- amazon-cognito-identity-js,
 * @aws-sdk/client-cognito-identity-provider, oidc-client-ts -- all make
 * network requests, all add a few hundred kilobytes to a bundle that has to
 * load beside valley paintings, and all of them are doing this. The hosted
 * managed login is already deployed in 20-api, so the whole flow is a
 * redirect out, a redirect back, and one fetch to exchange the code.
 *
 * The verifier never leaves this device. It is what stops an attacker who
 * intercepts the authorization code from being able to spend it.
 */

import { native } from '../native/platform';

const VERIFIER_KEY = 'valley.pkce.verifier';
const STATE_KEY = 'valley.pkce.state';

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

function randomBase64url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

/** RFC 7636 wants 43-128 characters; 32 random bytes base64url to exactly 43. */
export function createVerifier(): string {
  return randomBase64url(32);
}

export function createState(): string {
  return randomBase64url(16);
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/**
 * sessionStorage, not localStorage.
 *
 * These two values are only meaningful between the redirect out and the
 * redirect back, which is one browsing session by definition. localStorage
 * would leave a usable verifier lying about on a shared machine long after
 * sign-in had finished.
 */
/**
 * Where the verifier waits during the round trip.
 *
 * sessionStorage in a browser: the tab is the session. In the app, sign-in
 * happens in the system browser sheet, and the operating system is free to
 * stop the app behind it; a phone short on memory does exactly that, and a
 * verifier in sessionStorage would be gone when the parent comes back. So
 * the app keeps it in localStorage for the ten minutes a sign-in can take,
 * and `take` refuses anything older.
 */
const NATIVE_TTL_MS = 10 * 60 * 1000;
const SAVED_AT_KEY = 'valley.pkce.savedAt';

function store(): Storage {
  return native() ? localStorage : sessionStorage;
}

export const pkceStore = {
  save(verifier: string, state: string): void {
    const where = store();
    where.setItem(VERIFIER_KEY, verifier);
    where.setItem(STATE_KEY, state);
    if (where === localStorage) where.setItem(SAVED_AT_KEY, String(Date.now()));
  },
  take(): { verifier: string | null; state: string | null } {
    const where = store();
    let verifier = where.getItem(VERIFIER_KEY);
    let state = where.getItem(STATE_KEY);
    const savedAt = Number(where.getItem(SAVED_AT_KEY) || 0);
    where.removeItem(VERIFIER_KEY);
    where.removeItem(STATE_KEY);
    where.removeItem(SAVED_AT_KEY);
    if (where === localStorage && Date.now() - savedAt > NATIVE_TTL_MS) {
      verifier = null;
      state = null;
    }
    return { verifier, state };
  },
};
